import React, { useEffect, useMemo, useRef, useState } from 'react'

import getDesignTemplates from '@/services/DesignTemplateService/getDesignTemplates'
import getDesignTemplate from '@/services/DesignTemplateService/getDesignTemplate'
import deleteDesignTemplate from '@/services/DesignTemplateService/deleteDesignTemplate'
import patchDesignTemplate from '@/services/DesignTemplateService/patchDesignTemplate'

import Modal from '@/components/Modal/Modal'
import Heading from '@/components/Heading/Heading'
import SearchBar, { SearchBarFilters, SearchBarOptions } from '@/components/UiElements/SearchBar'
import Splash from '@/components/splash/Splash'
import Pagination from '@/components/UiElements/Pagination'
import { closeNotif, startNotif } from '@/components/DynamicForm/util/notifier'
import notifyError from '@/utils/notifyError'

import DesignTemplateItems from './DesignTemplateItems'
import DesignTemplateModal from './DesignTemplateModal'
import DesignTemplateEditorModal from './DesignTemplateEditorModal'
import { SavedTemplateRef } from './DesignTemplateModal'
import { DesignTemplateDetail, FlattedDesignTemplate } from '@/types/designTemplate'
import { getCompanyId } from '@/utils/getCompanyId'

const perPage = 20

interface DesignTemplatePageProps {
  hideHeader?: boolean
}

const DesignTemplatePage: React.FC<DesignTemplatePageProps> = ({ hideHeader = false }) => {
  const paginationRef = useRef<any>(null)
  const filteredRef = useRef<FlattedDesignTemplate[]>([])

  const [templates, setTemplates] = useState<FlattedDesignTemplate[]>([])
  const [filtered, setFiltered] = useState<FlattedDesignTemplate[]>([])
  const [total, setTotal] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)

  const [selectedTemplate, setSelectedTemplate] = useState<DesignTemplateDetail | null | undefined>(undefined)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  // Which row is being fetched, so the row itself can show it rather than a
  // toast that reads like something is being saved.
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [showInactive, setShowInactive] = useState<boolean>(false)

  const [designing, setDesigning] = useState<{
    template: DesignTemplateDetail
    sku: string
    sectionId?: string
  } | null>(null)

  const companyId = getCompanyId()

  const handleLoadTemplates = async () => {
    setLoading(true)
    // The listing already carries the element and placeholder counts, so the
    // design payload is only fetched when a template is actually opened.
    setTemplates(await getDesignTemplates())
    setLoading(false)
  }

  useEffect(() => {
    handleLoadTemplates()
  }, [companyId])

  useEffect(() => {
    const text = searchText.toLowerCase()
    const next = templates.filter((template) => {
      if (!showInactive && !template.isActive) return false
      if (!text) return true
      return `${template.name} ${template.description}`.toLowerCase().includes(text)
    })

    filteredRef.current = next
    setFiltered(next)
    paginationRef.current?.reset()
    setTotal(Math.ceil(next.length / perPage))
  }, [templates, searchText, showInactive])

  const handleGetPageData = (pageNumber: number) => {
    const start = (pageNumber - 1) * perPage
    return filteredRef.current.slice(start, start + perPage)
  }

  const handleOpenTemplate = async (template: FlattedDesignTemplate) => {
    setOpeningId(template.id)
    const detail = await getDesignTemplate(template.id)
    setOpeningId(null)

    if (!detail) {
      notifyError('Could not load that template.')
      return
    }

    setSelectedTemplate(detail)
    setIsModalOpen(true)
  }

  const handleAddTemplate = () => {
    setSelectedTemplate(null)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedTemplate(undefined)
  }

  const handleSaved = async () => {
    handleCloseModal()
    await handleLoadTemplates()
  }

  /**
   * Opens the design surface for a template that already knows its product.
   * Templates created before the product moved onto the form may not, in which
   * case the settings form asks for one.
   */
  const openDesigner = async (id?: string, skuHint?: string, sectionHint?: string) => {
    if (!id) return

    const detail = await getDesignTemplate(id)
    if (!detail) {
      notifyError('Could not load that template.')
      return
    }

    const sku = skuHint || detail.data?.origin?.sku
    const sectionId = sectionHint || detail.data?.origin?.section_id
    if (!sku) {
      notifyError('Choose a product for this template first, then design it.')
      setSelectedTemplate(detail)
      setIsModalOpen(true)
      return
    }

    setDesigning({ template: detail, sku, sectionId })
  }

  const handleSavedAndDesign = async (saved: SavedTemplateRef) => {
    handleCloseModal()
    await handleLoadTemplates()
    await openDesigner(saved.id, saved.sku, saved.sectionId)
  }

  const handleDesignTemplate = async (template: FlattedDesignTemplate) => {
    setOpeningId(template.id)
    await openDesigner(template.id)
    setOpeningId(null)
  }

  const handleDesignSaved = async () => {
    await handleLoadTemplates()
  }

  const handleDelete = async (id: string) => {
    const toast = startNotif('Deleting template...')
    const result = await deleteDesignTemplate(id)

    if (result === null) {
      closeNotif(toast, 'Failed to delete the template.', { type: 'error' })
      return
    }

    closeNotif(toast, 'Template deleted.')
    await handleLoadTemplates()
  }

  const handleToggleActive = async (template: FlattedDesignTemplate) => {
    const nextActive = !template.isActive
    const toast = startNotif(nextActive ? 'Activating template...' : 'Deactivating template...')
    const result = await patchDesignTemplate(template.id, { is_active: nextActive })

    if (result === null) {
      closeNotif(toast, 'Failed to update the template.', { type: 'error' })
      return
    }

    closeNotif(toast, nextActive ? 'Template activated.' : 'Template deactivated.')
    await handleLoadTemplates()
  }

  const filters = useMemo<SearchBarFilters[]>(
    () => [
      {
        label: 'Show Inactive Templates',
        onClick: (value: boolean) => setShowInactive(value),
        count: templates.filter((template) => !template.isActive).length,
      },
    ],
    [templates]
  )

  const options = useMemo<SearchBarOptions[]>(
    () => [
      {
        label: (
          <span className="flex flex-row gap-md">
            <span className="font-icon-solid text-primary">add</span>
            New Template
          </span>
        ),
        onClick: handleAddTemplate,
      },
    ],
    []
  )

  return (
    <div className={'min-h-[100%] relative'}>
      {designing && (
        <DesignTemplateEditorModal
          template={designing.template}
          sku={designing.sku}
          sectionId={designing.sectionId}
          isOpen={!!designing}
          onClose={() => setDesigning(null)}
          onSaved={handleDesignSaved}
        />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedTemplate?.id ? 'Edit design template' : 'Add design template'}
        closeOnOutsideClick={false}
      >
        {isModalOpen && (
          <DesignTemplateModal
            currentTemplate={selectedTemplate}
            onClose={handleCloseModal}
            onSaved={handleSaved}
            onSaveAndDesign={handleSavedAndDesign}
          />
        )}
      </Modal>

      {!hideHeader && (
        <Heading
          title={'Design Templates'}
          description={'Pre-designed layouts customers start from, with placeholders they fill in.'}
        />
      )}

      <div className={'sticky top-[48px] bg-white z-[10]'}>
        <div className={'h-[48px] absolute top-[-48px] w-full bg-white z-[-1]'}></div>
        <SearchBar
          placeholder={'Search by template name'}
          searchText={searchText}
          onChange={(text) => setSearchText(text)}
          filters={filters}
          options={options}
        />
      </div>

      {loading && <Splash />}

      {filtered.length > 0 && total > 0 ? (
        <Pagination
          ref={paginationRef}
          theme={(data) => (
            <DesignTemplateItems
              template={data}
              busy={openingId === data.id}
              callBack={handleOpenTemplate}
              onDesign={handleDesignTemplate}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          )}
          total={total}
          offset={150}
          getPageData={handleGetPageData}
          className={'pt-xxl'}
        />
      ) : (
        !loading && (
          <div
            className={
              'flex flex-row justify-center items-center px-mx py-xl text-gray-300 bg-gray-50 rounded-md mt-xxl gap-lg h-[150px]'
            }
          >
            <p>
              {templates.length === 0 ? (
                <span>No design templates yet. Create one to give customers a head start.</span>
              ) : (
                <>
                  <span>No results match your current filters. Try </span>
                  <span
                    className={'text-gray-300 underline cursor-pointer hover:text-gray-900 transition-all'}
                    onClick={() => {
                      setSearchText('')
                      setShowInactive(false)
                    }}
                  >
                    resetting some filters
                  </span>
                  <span> to see more options.</span>
                </>
              )}
            </p>
          </div>
        )
      )}
    </div>
  )
}

export default DesignTemplatePage
